import { ChangeEventHandler, useState } from 'react';
import { useSearchParams } from 'react-router';
import _ from 'lodash';

import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import Pagination from '../../shared/pagination/Pagination';
import { ExportSKUsForm } from './ExportSKUsForm';
import { IapProductsTable } from './IapProductsTable';
import SkuManagementHeader from './SkuManagementHeader';
import { Loading, Section } from '../../shared';

import {
    useIapProducts,
    useSyncIapProductStatuses,
} from '../../hooks/iapProducts';

import {
    filterIapProductsByActive,
    filterIapProductsBySearch,
    filterIapProductsByStore,
    filterIapProductsByStorefrontStatus,
    getTotalPageCount,
    paginate,
    SkuSortByType,
    sortIapProducts,
} from './utils';

export function SkuManagementPage() {
    const { products, isLoading, error, refetch } = useIapProducts();
    const { syncStatuses, isSyncing, errorMessage } =
        useSyncIapProductStatuses();

    const [searchString, setSearchString] = useState(
        localStorage.getItem('skuSearchString') ?? '',
    );
    const [filterByStore, setFilterByStore] = useState(
        localStorage.getItem('filterByStore') ?? 'all',
    );
    const [filterByActive, setFilterByActive] = useState(
        localStorage.getItem('filterByActive') ?? 'all',
    );
    const [filterByStorefrontStatus, setFilterByStorefrontStatus] = useState(
        localStorage.getItem('filterByStorefrontStatus') ?? 'all',
    );
    const [isSortAscend, setIsSortAscend] = useState(false);
    const [sortBy, setSortBy] = useState<SkuSortByType>();
    const [syncingKey, setSyncingKey] = useState<string | null>(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get('page')) || 1;

    const resetPage = () => {
        setSearchParams((prev) => {
            prev.delete('page');
            return prev;
        });
    };

    const onSearchStringChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const value = e.target.value;
        localStorage.setItem('skuSearchString', value);
        setSearchString(value);
        resetPage();
    };

    const onFilterChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const { name, value } = e.target;
        if (name === 'store') {
            setFilterByStore(value);
        }
        if (name === 'active') {
            setFilterByActive(value);
        }
        if (name === 'storefrontStatus') {
            setFilterByStorefrontStatus(value);
        }
        const itemKey =
            'filterBy' + name.charAt(0).toUpperCase() + name.substring(1);
        localStorage.setItem(itemKey, value);
        resetPage();
    };

    const onClickSort = (newSortBy: SkuSortByType) => {
        if (sortBy === newSortBy) {
            if (isSortAscend) {
                setSortBy(undefined);
            }
            return setIsSortAscend(!isSortAscend);
        }
        return setSortBy(newSortBy);
    };

    const handleSync = (sku: string, store: string) => {
        setSyncingKey(`${store}:${sku}`);
        syncStatuses(
            { skus: [sku], store },
            { onSettled: () => setSyncingKey(null) },
        );
    };

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    const filteredProducts = _.flow([
        (list) => filterIapProductsBySearch(list, searchString),
        (list) => filterIapProductsByStore(list, filterByStore),
        (list) => filterIapProductsByActive(list, filterByActive),
        (list) =>
            filterIapProductsByStorefrontStatus(list, filterByStorefrontStatus),
    ])(products ?? []);
    const filteredSortedProducts = sortIapProducts(
        filteredProducts,
        sortBy,
        isSortAscend,
    );

    const totalPageCount = getTotalPageCount(filteredSortedProducts.length);
    const effectivePage = Math.min(page, totalPageCount);
    const pageProducts = paginate(filteredSortedProducts, effectivePage);

    return (
        <>
            <Section title="Export SKUs">
                <ExportSKUsForm />
            </Section>
            <Section>
                {errorMessage ? (
                    <div className="status invalid">{errorMessage}</div>
                ) : null}
                <SkuManagementHeader
                    count={filteredSortedProducts.length}
                    searchString={searchString}
                    onSearchStringChange={onSearchStringChange}
                    filterValues={{
                        store: filterByStore,
                        active: filterByActive,
                        storefrontStatus: filterByStorefrontStatus,
                    }}
                    onFilterChange={onFilterChange}
                    onRefresh={() => refetch()}
                />
                <IapProductsTable
                    products={pageProducts}
                    onSync={handleSync}
                    syncingKey={isSyncing ? syncingKey : null}
                    sortBy={sortBy}
                    isSortAscend={isSortAscend}
                    onClickSort={onClickSort}
                />
                {totalPageCount > 1 && (
                    <Pagination
                        currentPage={effectivePage}
                        totalPageCount={totalPageCount}
                    />
                )}
            </Section>
        </>
    );
}
